/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-underscore-dangle */
import React, { useState, useRef, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Card, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

import { useGetSettings } from "api/queries/user/useGetSettings";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import Alert from "components/molecules/Alert/Alert";
import DashboardGrid from "pages/common/Dashboard/components/common/DashboardGrid/DashboardGrid";
import SplashLoader from "components/atoms/SplashLoader/SplashLoader";
import AnimatedLoader from "components/atoms/AnimatedLoader/AnimatedLoader";
import RefreshCache from "components/molecules/RefreshCache/RefreshCache";
import DashboardTour from "components/tours/dashboard/DashboardTour";
import usePermissions from "hooks/usePermissions";
import MapWidget from "./components/common/widgets/MapWidget/MapWidget";
import StatusWidget from "./components/common/widgets/StatusWidget";
import WidgetDialog from "./components/common/WidgetDialog/WidgetDialog";
import ExceptionsWidget from "./components/shipper/widgets/ExceptionsWidget";
import RecentUpdatesWidget from "./components/shipper/widgets/RecentUpdatesWidget";
import RecentDeliveriesWidget from "./components/shipper/widgets/RecentDeliveriesWidget";
import TasksWidget from "./components/common/widgets/TasksWidget";
import QuickActionsWidget from "./components/common/widgets/QuickActionsWidget";
import CalendarWidget from "./components/common/widgets/CalendarWidget/CalendarWidget";
import RecentlyVisitedWidget from "./components/shipper/widgets/RecentlyVisitedWidget";
import UserWidget from "./components/common/widgets/UserWidget/UserWidget";
import "./Dashboard.scss";

const Dashboard = function (props) {
  const { firstTimeAppLoaded, updateAppLoadStatus, isSidebarShrink } = props;

  const dashboardGrid = useRef(null);
  const { permissions } = usePermissions();
  const queryClient = useQueryClient();
  const permissionData = JSON.parse(permissions || "{}");
  const { payment_location, first_name, last_name } = permissionData;
  const paymentLocations = payment_location?.locations;

  const [errors] = useState({});
  const [isLayoutLocked, setIsLayoutLocked] = useState(true);
  const [showWidgetDialog, setShowWidgetDialog] = useState(false);
  const [widgetToEditId, setWidgetToEditId] = useState(null);
  const [refreshToken, setRefreshToken] = useState(false);

  const { data: userSettings, isFetching: isFetchingUserSettings, dataUpdatedAt } = useGetSettings();

  useEffect(() => {
    if (!isFetchingUserSettings) updateAppLoadStatus(false);
  }, [isFetchingUserSettings]);

  const handleClickRefresh = () => {
    if (!refreshToken) setRefreshToken(true);
    else queryClient.invalidateQueries(userQueryKeys.userWidgets[0]);
  };

  /**
   * Opens the widget dialog
   * - if widgetId is null, it will open the dialog to add a new widget
   * - if widgetId is present, it will open the dialog to edit the widget
   * @param {*} widgetId - the widget id to edit
   */
  const handleWidgetDialogOpen = (widgetId = null) => {
    setWidgetToEditId(widgetId);
    setShowWidgetDialog(true);
  };

  /**
   * Closes the widget dialog
   * - resets the widgetToEditId to null
   */
  const handleWidgetDialogClose = () => {
    setWidgetToEditId(null);
    setShowWidgetDialog(false);
  };

  /**
   * Renders the widget based on the widget type
   * @param {*} widget - the widget to render
   * @returns a <Card> component with the widget content with a data-grid attribute to be used by react-grid-layout
   */
  const renderWidget = (widget) => {
    // Set the base props for the widget
    const baseProps = {
      isLayoutLocked,
      handleWidgetDialogOpen,
      widgetId: widget.id,
    };

    // Check if the widgets payment location exists in the users permissions, if not then we don't render the widget
    if (widget?.filters?.payment_location) {
      const paymentLocationExists = paymentLocations?.find((pl) => pl.value === widget?.filters?.payment_location);
      if (!paymentLocationExists) return;
    }

    // Return the widget <Card> based on the widget type
    switch (widget.type) {
      case "Status":
        return (
          <Card
            key={widget.id}
            data-grid={{ x: widget.position?.x || 0, y: widget.position?.y || 0, w: 1, h: 2, isResizable: false }}
            className={`border-round ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            <StatusWidget
              widgetContent={widget.content}
              paymentLocation={widget?.filters?.payment_location}
              refreshToken={refreshToken}
              {...baseProps}
            />
          </Card>
        );
      case "Greeting":
        return (
          <Card
            key={widget.id}
            data-grid={{
              x: widget.position?.x || 0,
              y: widget.position?.y || 0,
              w: 1,
              h: 2,
              static: true,
            }}
            className={`border-round ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            <UserWidget
              isLayoutLocked={isLayoutLocked}
              setIsLayoutLocked={setIsLayoutLocked}
              firstName={first_name}
              lastName={last_name}
              openWidgetDialog={() => {
                setShowWidgetDialog(true);
              }}
            />
          </Card>
        );
      case "Map":
        return (
          <Card
            key={widget.id}
            data-grid={{ x: widget.position?.x || 0, y: widget.position?.y || 0, w: 4, h: 8, isResizable: false }}
            className={`border-round map-wrapper ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            <MapWidget {...baseProps} />
          </Card>
        );
      case "List":
        return (
          <Card
            key={widget.id}
            data-grid={{
              x: widget.position?.x || 0,
              y: widget.position?.y || 0,
              w: widget.position?.w || 1,
              h: widget.position?.h || 8,
              minH: 4,
              maxH: 15,
            }}
            className={`border-round overflow-hidden ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            {renderListWidget(widget, baseProps)}
          </Card>
        );
      case "QuickActions":
        return (
          <Card
            key={widget.id}
            data-grid={{
              x: widget.position?.x || 0,
              y: widget.position?.y || 0,
              w: widget.position?.w || 1,
              h: widget.position?.h || 3,
              maxW: 1,
            }}
            className={`border-round ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            <QuickActionsWidget {...baseProps} widgetContent={widget.content} />
          </Card>
        );
      case "Calendar":
        return (
          <Card
            key={widget.id}
            data-grid={{
              x: widget.position?.x || 0,
              y: widget.position?.y || 0,
              w: widget.position?.w || 2,
              h: widget.position?.h || 6,
              maxW: 2,
              maxH: 10,
              minH: 4,
              minW: 2,
            }}
            className={`border-round overflow-auto ${isLayoutLocked ? "shadow-none" : ""}`}
          >
            <CalendarWidget {...baseProps} widgetFilters={widget.filters} />
          </Card>
        );
      default:
        return null;
    }
  };

  /**
   * Render the list widget based on the widget content
   * @param {*} widget - the widget to render
   * @param {*} baseProps - the base props for the widget
   * @returns the relevant list widget component
   */
  const renderListWidget = (widget, baseProps) => {
    switch (widget.content) {
      case "Recent updates":
        return (
          <RecentUpdatesWidget
            {...baseProps}
            paymentLocation={widget?.filters?.payment_location}
            widgetDataSource={widget.data_source}
          />
        );
      case "Recent deliveries":
        return <RecentDeliveriesWidget {...baseProps} paymentLocation={widget?.filters?.payment_location} />;
      case "Exceptions":
        return <ExceptionsWidget {...baseProps} paymentLocation={widget?.filters?.payment_location} />;
      case "Tasks":
        return <TasksWidget {...baseProps} paymentLocation={widget?.filters?.payment_location} />;
      case "Recently visited":
        return <RecentlyVisitedWidget {...baseProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <RefreshCache dataUpdatedAt={dataUpdatedAt} onRefresh={handleClickRefresh} isLoading={isFetchingUserSettings} />

      <DashboardTour isLayoutLocked={isLayoutLocked} setIsLayoutLocked={setIsLayoutLocked} />
      <div className="dashboard-container">
        {errors.notifications && errors.recentlyDelivered && errors.cargobaseUser && errors.exceptions ? (
          <Alert className="mb-0" variant="danger">
            <FormattedMessage
              id="dashboard.contentLoadingFailed"
              defaultMessage="Content could not be loaded. Please check your internet connection and try again."
            />
          </Alert>
        ) : (
          <>
            {isFetchingUserSettings ? (
              <>
                {firstTimeAppLoaded ? (
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <div className="spinner-center">
                          <AnimatedLoader />
                        </div>
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <SplashLoader />
                )}
              </>
            ) : (
              <>
                <DashboardGrid
                  ref={dashboardGrid}
                  isLayoutLocked={isLayoutLocked}
                  widgets={userSettings?.dashboard_widgets}
                  isSidebarShrink={isSidebarShrink}
                >
                  {userSettings?.dashboard_widgets.map((widget) => renderWidget(widget))}
                </DashboardGrid>
                <WidgetDialog
                  isOpen={showWidgetDialog}
                  close={handleWidgetDialogClose}
                  isLayoutLocked={isLayoutLocked}
                  widgetId={widgetToEditId}
                  dashboardWidgets={userSettings?.dashboard_widgets}
                  refreshToken={refreshToken}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;

Dashboard.propTypes = {
  firstTimeAppLoaded: PropTypes.bool,
  updateAppLoadStatus: PropTypes.func,
  isSidebarShrink: PropTypes.bool,
};

Dashboard.defaultProps = {
  firstTimeAppLoaded: true,
  updateAppLoadStatus: null,
  isSidebarShrink: false,
};
